import React, { useContext, useMemo, useState } from 'react'
import PropTypes from 'prop-types'

import moment from 'moment'
import 'moment/locale/sq'

import { TouchableOpacity, useWindowDimensions, View } from 'react-native'

import { VFButton, VFText } from '@vfgroup-oneplatform/foundation/Components'
import { Calendar, LocaleConfig } from 'react-native-calendars'
import SelectionList from '@vfgroup-oneplatform/foundation/Components/SelectionList'

import { Colors, useTheme } from '@vfgroup-oneplatform/foundation/Components/Themes'

import { INSTALL } from '../../Utils/payment'

import UnlimitedContext from '../../Context'

import styles from './InstallationStep.Styles'

const dateFormat = 'YYYY-MM-DD'

LocaleConfig.locales.al = {
  monthNames: [
    'Janar',
    'Shkurt',
    'Mars',
    'Prill',
    'Maj',
    'Qershor',
    'Korrik',
    'Gusht',
    'Shtator',
    'Tetor',
    'Nentor',
    'Dhjetor'
  ],
  monthNamesShort: [
    'Jan',
    'Shku',
    'Mar',
    'Pri',
    'Maj',
    'Qer',
    'Korr',
    'Gusht',
    'Shta',
    'Tet',
    'Nen',
    'Dhje'
  ],
  dayNames: [
    'E Diele',
    'E Hene',
    'E Marte',
    'E Merkure',
    'E Enjte',
    'E Premte',
    'E Shtune'
  ],
  dayNamesShort: ['D', 'H', 'M', 'M', 'E', 'P', 'S'],
  today: 'Sot'
}

const getSelectedTimeIdx = (obj, date, time) => {
  if (!obj || Object.keys(obj).length === 0 || !date || !time) {
    return null
  }
  const selectedDate = obj[date]
  if (!Array.isArray(selectedDate)) {
    return null
  }
  const idx = selectedDate.findIndex(el => el === time)
  if (idx < 0) {
    return null
  }
  return idx
}

const InstallationStep = ({ goNext, changeInstallData }) => {
  const { width } = useWindowDimensions()

  const theme = useTheme()

  const calendarTheme = useMemo(() => {
    return {
      dotColor: theme.colors.activeColor,
      dayTextColor: theme.colors.textColor,
      arrowColor: theme.colors.activeColor,
      monthTextColor: theme.colors.textColor,
      todayTextColor: theme.colors.activeColor,
      textSectionTitleColor: theme.colors.textColor,
      calendarBackground: theme.colors.cardBackgroundColor,
      textDisabledColor: theme.colors.cardBackgroundColorTwo,
      disabledArrowColor: theme.colors.cardBackgroundColorTwo
    }
  }, [
    theme.colors.textColor,
    theme.colors.activeColor,
    theme.colors.cardBackgroundColor,
    theme.colors.cardBackgroundColorTwo
  ])

  const {
    data: {
      language,
      installCalendar,
      installationData: { date, time }
    }
  } = useContext(UnlimitedContext)

  const isNextDisabled = !date || !time

  const [timeIndex, setTimeIndex] = useState(() =>
    getSelectedTimeIdx(installCalendar, date, time)
  )

  if (language === 'al') {
    moment.locale('sq')
    LocaleConfig.defaultLocale = 'al'
  }

  const handleDateChange = (selectedDate, selectedTime) => {
    changeInstallData({
      installDate: selectedDate ?? '',
      installTime: selectedTime ?? ''
    })
  }

  const minDate = moment().add(1, 'day').format(dateFormat)

  const maxDate = installCalendar
    ? moment(
        Object.keys(installCalendar)[Object.keys(installCalendar).length - 1]
      ).format(dateFormat)
    : null

  const getMarkedDates = () => {
    return {
      [moment().format(dateFormat)]: {
        color: Colors.blue_two
      },
      [date ?? '']: {
        selected: true,
        selectedColor: Colors.blue_two
      }
    }
  }

  const stylizedCustomSlots = useMemo(() => {
    const timeSlots = installCalendar[date]
    if (!Array.isArray(timeSlots)) {
      return []
    }
    const elementWidth = (width - 56) / (timeSlots.length ?? 1)

    return timeSlots.map((slot, index) => {
      return {
        slot,
        index,
        containerStyle: [
          styles.bgWhite(theme),
          styles.verticalPadding,
          { width: elementWidth },
          index === 0 ? null : { marginLeft: 12 }
        ]
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, installCalendar, width])

  const handleTimeSlotSelect = idx => {
    setTimeIndex(idx)
    if (!date) {
      return
    }
    const timeSlot = installCalendar[date][idx]
    if (!timeSlot) {
      return
    }
    handleDateChange(date, timeSlot)
  }

  return (
    <View style={styles.installationStep}>
      <View style={styles.paddingHorizontal}>
        <VFText style={styles.header} i18nKey="unlimited_installation_title" />
        <VFText
          style={styles.description}
          i18nKey="unlimited_installation_description"
        />
      </View>
      <View style={styles.calendarWrapper}>
        <Calendar
          key={theme.isDark}
          hideExtraDays
          minDate={minDate}
          maxDate={maxDate}
          markingType="custom"
          theme={calendarTheme}
          style={styles.calendar}
          markedDates={getMarkedDates()}
          disableAllTouchEventsForDisabledDays
          onDayPress={day => handleDateChange(day.dateString, time)}
        />
        {date ? (
          <>
            <VFText style={styles.preferTime} i18nKey="unlimited_prefer_time" />
            <SelectionList
              customItem={<SlotOption />}
              options={stylizedCustomSlots}
              initialSelectedIndex={timeIndex}
              onSelectOption={handleTimeSlotSelect}
            />
          </>
        ) : null}
      </View>
      <View style={styles.feeHolder(theme)}>
        <View style={styles.flex}>
          <VFText
            style={styles.feeTitle}
            i18nKey="unlimited_installation_fee_title"
          />
          <VFText
            style={styles.feeTitle}
            placeHolders={[INSTALL.value]}
            i18nKey="unlimited_summary_amount"
          />
        </View>
        <VFText
          style={styles.feeSubtitle}
          i18nKey="unlimited_installation_fee_subtitle"
        />
      </View>
      <View style={styles.paddingHorizontal}>
        {/* Button */}
        <VFButton
          onPress={goNext}
          disabled={isNextDisabled}
          title="unlimited_next_step_button"
        />
      </View>
    </View>
  )
}

const SlotOption = props => {
  const getTimeOfDay = idx => {
    switch (idx) {
      case 0:
        return 'unlimited_morning_time'
      case 1:
        return 'unlimited_noon_time'
      case 2:
        return 'unlimited_afternoon_time'
    }
  }

  return (
    <TouchableOpacity
      activeOpacity={1}
      onPress={props.onPress}
      style={[
        styles.roundedBorders,
        props.isActive ? styles.selectedPlan : styles.optionWrapper,
        ...props.option.containerStyle
      ]}
    >
      <VFText
        style={styles.timeOfDay}
        i18nKey={getTimeOfDay(props.option.index)}
      />
      <VFText i18nKey={props.option.slot} />
    </TouchableOpacity>
  )
}

SlotOption.propTypes = {
  onPress: PropTypes.func,
  isActive: PropTypes.bool,
  option: PropTypes.objectOf({
    slot: PropTypes.string,
    index: PropTypes.number
  })
}

InstallationStep.propTypes = {
  goNext: PropTypes.func,
  changeInstallData: PropTypes.func
}

InstallationStep.defaultProps = {
  goNext: () => {},
  changeInstallData: () => {}
}

export default InstallationStep
